import React, {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

const TabStorageContext = createContext<{
  storageValue: Record<string, unknown>;
  updateTabStorage: (value: any) => void;
}>({storageValue: {} as never, updateTabStorage: () => void 0});

enum MessageTypes {
  tabOpened,
  valueUpdate,
}

/**
 * WARNING: This hook currently use a single global object to store the data.
 * This means its not scalable for a wider use-cases as changes to any keys will trigger a re-render for all components using this hook.
 * In the future, we should consider moving to a more scalable solution where it should be possible to pass the keys that should trigger a re-render.
 */
export function useTabStorage<T = unknown>() {
  return useContext(
    TabStorageContext as React.Context<{
      storageValue: Partial<T>;
      updateTabStorage: (value: Partial<T>) => void;
    }>,
  );
}

export const TabStorageProvider = ({children}: PropsWithChildren) => {
  const [storageValue, setStorageValue] = useState<Record<string, unknown>>({});
  const broadcastChannelRef = useRef<BroadcastChannel | null>(null);
  const tabOpenedRequestSent = useRef<boolean>(false);

  // eslint-disable-next-line local-rules/too-complex-useeffects
  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') {
      return;
    }

    const bc = new BroadcastChannel('AY_Tab_storage');

    bc.onmessage = value => {
      if (value.data.type === MessageTypes.tabOpened) {
        bc.postMessage({
          type: MessageTypes.valueUpdate,
          data: storageValue,
        });
        return;
      }

      if (
        value.data.type === MessageTypes.valueUpdate &&
        Object.keys(value.data.data).length
      ) {
        const newValue = {...storageValue, ...value.data.data};
        // eslint-disable-next-line local-rules/set-state-in-use-effect
        setStorageValue(newValue);
      }
    };

    if (!tabOpenedRequestSent.current) {
      bc.postMessage({type: MessageTypes.tabOpened});
    }

    tabOpenedRequestSent.current = true;
    broadcastChannelRef.current = bc;

    return () => {
      broadcastChannelRef.current = null;
      bc.close();
    };
  }, [storageValue]);

  const updateTabStorage = useCallback(
    (value: Record<string, unknown>) => {
      const newValue = {...storageValue, ...value};
      setStorageValue(newValue);

      broadcastChannelRef.current?.postMessage({
        type: MessageTypes.valueUpdate,
        data: newValue,
      });
    },
    [storageValue],
  );

  const value = useMemo(() => {
    return {
      storageValue,
      updateTabStorage,
    };
  }, [storageValue, updateTabStorage]);

  return (
    <TabStorageContext.Provider value={value}>
      {children}
    </TabStorageContext.Provider>
  );
};
